import { Temporal } from '@js-temporal/polyfill';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import { CREW_CLASS, type Shift } from '../api/crew.ts';
import type { TimeOffEntry } from '../api/time-off.ts';
import { jobsQuery, updateJob } from '../api/jobs.ts';
import { meQuery } from '../api/me.ts';
import {
  assignTasks,
  bulkShifts,
  clearTasks,
  rosterPageQuery,
  type BulkAction,
} from '../api/roster.ts';
import type { Task } from '../api/tasks.ts';
import { venuesQuery } from '../api/venues.ts';
import { AddButton } from '../components/action-buttons.tsx';
import { Button } from '../components/button.tsx';
import { icons } from '../components/icons.ts';
import { PageHeader } from '../components/page-header.tsx';
import { operationalSpan } from '../roster-layout.ts';
import { hoursInView, peopleRows, taskBands, type TaskBand } from '../roster-rows.ts';
import {
  barStyle,
  dayName,
  GroupRow,
  initials,
  OfferBar,
  ShiftBar,
  TaskBar,
  TimelineHead,
  TimelineOverlay,
  TrackRow,
  type Placed,
  zoneName,
} from './roster-bars.tsx';
import { MoveCallout, MoveLine, TrackOverlay, useRosterDrag } from './roster-drag.tsx';
import { timeOffTitle, timeOffWhen } from './time-off.tsx';
import { RosterKey } from './roster-key.tsx';
import { RosterPopover, type Opened, type Outcome } from './roster-popovers.tsx';

// CB-REQ OPS-03, CRW-12 and CRW-04 at v1.998, CB-CONV §12 crew part 4B, drawn as the operator's
// roster design of 2026-10-26 (docs/designs/roster-page.md §1 – §2): the one page. Tasks above, a
// band per job with its client, venue and lead; then everyone on the crew list, busy first, with
// their hours in view. Any bar opens in a popover, and Select mode does several at once: tasks onto
// a shift, everyone off tasks, and publishing, confirming or cancelling shifts. The range and
// filters are in the URL, so a job's "Open in roster" is a link. Drawing, dragging and resizing are
// 4C's; crew folders, the right-click menu, hover and clone are not yet in the spec.

export const ROSTER_DAYS = [1, 3, 7, 14] as const;
export type RosterDays = (typeof ROSTER_DAYS)[number];
export interface RosterSearch {
  from?: string;
  days?: RosterDays;
  job?: string;
  venue?: string;
}

/** Hours between labels, and the width of a day, by how many days are in view. */
const TICK_EVERY: Record<RosterDays, number> = { 1: 1, 3: 3, 7: 6, 14: 24 };
const DAY_WIDTH: Record<RosterDays, number> = { 1: 920, 3: 520, 7: 300, 14: 170 };

export type Picked = { kind: 'task' | 'shift'; ids: string[] };

export function RosterScreen() {
  const search = useSearch({ strict: false }) as RosterSearch;
  const navigate = useNavigate();
  const me = useQuery(meQuery);
  const zone = me.data?.tenant.defaultTz ?? 'Australia/Melbourne';
  const date = search.from ?? operationalToday(zone);
  const days = search.days ?? 1;
  const go = (changes: { [K in keyof RosterSearch]?: RosterSearch[K] | undefined }) =>
    void navigate({ to: '/roster', search: strip({ ...search, ...changes }) });
  const span = operationalSpan(date, days, zone);
  const filter = {
    jobIds: search.job ? [search.job] : [],
    venueIds: search.venue ? [search.venue] : [],
  };
  const page = useQuery(rosterPageQuery(span.from.toString(), span.to.toString(), filter));
  // For the filters and each band's client and lead; without jobs.view the bands name their job.
  const jobs = useQuery(jobsQuery);
  const venues = useQuery(venuesQuery);
  const queryClient = useQueryClient();
  const mayManage = me.data?.capabilities.includes('shifts.manage') ?? false;
  // CRW-02 at v1.9106: the office enters time off with crew.manage, and decides leave apart.
  const mayCrew = me.data?.capabilities.includes('crew.manage') ?? false;
  const mayApprove = me.data?.capabilities.includes('leave.approve') ?? false;
  const mayLead = me.data?.capabilities.includes('jobs.manage') ?? false;
  const seesPrices = me.data?.canSeePrices ?? false;
  const [opened, setOpened] = useState<Opened | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const close = () => setOpened(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['roster'] });
    await queryClient.invalidateQueries({ queryKey: ['shifts'] });
    await queryClient.invalidateQueries({ queryKey: ['shift-offers'] });
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['jobs'] });
  };

  const data = page.data;
  // OPS-03 at v1.9105 (crew part 4C): draw, drag and resize, off in Select mode and while a
  // popover is open.
  const drag = useRosterDrag({
    data,
    span,
    zone,
    days,
    enabled: mayManage && !selecting && !opened,
    refresh,
  });
  const bands = taskBands(data?.tasks ?? []);
  const rows = data ? peopleRows(data) : [];
  const nameOf = (id: string) => {
    const task = data?.tasks.find((t) => t.id === id);
    if (task) return task.description;
    const s = data?.shifts.find((x) => x.id === id);
    return s
      ? `${s.crewMember?.name ?? 'Unfilled'}, ${s.window.localStart.slice(11, 16)} – ${s.window.localEnd.slice(11, 16)}`
      : 'One';
  };

  const assign = useMutation({
    mutationFn: (v: { taskIds: string[]; shiftId: string; confirm: boolean }) =>
      assignTasks(v.taskIds, v.shiftId, v.confirm),
    onSuccess: (result, v) => {
      setPicked(null);
      setOutcome({
        done: `Put ${result.assigned.length} on.`,
        refused: result.refused.map((r) => ({ name: nameOf(r.taskId), reason: r.reason })),
      });
      if (result.mismatched.length > 0) {
        setOpened({
          kind: 'mismatch',
          shiftId: v.shiftId,
          taskIds: v.taskIds,
          items: result.mismatched,
        });
      } else if (v.confirm) close();
      void refresh();
    },
  });
  const clear = useMutation({
    mutationFn: (taskIds: string[]) => clearTasks(taskIds),
    onSuccess: (result) => {
      setPicked(null);
      setOutcome({ done: `Everyone off ${result.length}.`, refused: [] });
      void refresh();
    },
  });
  const bulk = useMutation({
    mutationFn: (v: { action: BulkAction; shiftIds: string[] }) => bulkShifts(v.action, v.shiftIds),
    onSuccess: (result, v) => {
      setPicked(null);
      close();
      const word = { publish: 'Published', confirm: 'Confirmed', cancel: 'Cancelled' }[v.action];
      setOutcome({
        done: `${word} ${result.done.length}.`,
        refused: result.refused.map((r) => ({ name: nameOf(r.shiftId), reason: r.reason })),
      });
      void refresh();
    },
  });
  const failed = assign.error ?? clear.error ?? bulk.error;

  function toggle(kind: Picked['kind'], id: string) {
    setOutcome(null);
    // Tasks and shifts are never ticked together.
    if (!picked || picked.kind !== kind) return setPicked({ kind, ids: [id] });
    const ids = picked.ids.includes(id) ? picked.ids.filter((x) => x !== id) : [...picked.ids, id];
    setPicked(ids.length ? { kind, ids } : null);
  }
  const onTask = (id: string) => (selecting ? toggle('task', id) : setOpened({ kind: 'task', id }));
  const onShift = (s: Shift) => {
    if (!selecting) return setOpened({ kind: 'shift', id: s.id });
    // With tasks ticked, a shift is where they go.
    if (picked?.kind === 'task') {
      return assign.mutate({ taskIds: picked.ids, shiftId: s.id, confirm: false });
    }
    toggle('shift', s.id);
  };
  const isPicked = (kind: Picked['kind'], id: string) =>
    selecting ? picked?.kind === kind && picked.ids.includes(id) : undefined;
  const move = (by: number) =>
    go({
      from: Temporal.PlainDate.from(date)
        .add({ days: by * days })
        .toString(),
    });

  const year = Temporal.PlainDate.from(span.days.at(-1)!).year;
  const when =
    days === 1
      ? `${dayName(date)} ${year}`
      : `${dayName(span.days[0]!)} – ${dayName(span.days.at(-1)!)} ${year}`;
  const counts = data
    ? [
        plural(bands.filter((b) => b.job).length, 'job'),
        plural(data.tasks.length, 'task'),
        plural(data.shifts.length, 'shift'),
        plural(data.offers.length, 'open offer'),
      ].join(' · ')
    : '';
  const every = TICK_EVERY[days];
  const cols = (days * 24) / every;
  const updated = page.dataUpdatedAt
    ? Temporal.Instant.fromEpochMilliseconds(page.dataUpdatedAt)
        .toZonedDateTimeISO(zone)
        .toPlainTime()
        .toString({ smallestUnit: 'minute' })
    : null;
  const busyCount = rows.filter((r) => !r.sub && r.shifts.length + r.offers.length > 0).length;
  const partFilled = (data?.tasks ?? []).filter(
    (t) => t.status === 'open' && t.fill === 'part',
  ).length;
  const doneCount = (data?.tasks ?? []).filter((t) => t.status === 'done').length;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        kicker="Crew"
        title="Roster"
        meta={`${when} · 05:00 → 05:00 ${zoneName(zone)}${counts ? ` · ${counts}` : ''}`}
        actions={
          mayManage ? (
            <>
              <AddButton noun="Shift" onClick={() => setOpened({ kind: 'add', what: 'shift' })} />
              <AddButton
                noun="Task"
                variant="quiet"
                onClick={() => setOpened({ kind: 'add', what: 'task' })}
              />
              <AddButton
                noun="Offer"
                variant="quiet"
                onClick={() => setOpened({ kind: 'add', what: 'offer' })}
              />
            </>
          ) : null
        }
      />
      <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-divider pb-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="flex items-center gap-1">
            <Button
              variant="quiet"
              className="small-btn"
              onClick={() => move(-1)}
              aria-label="Earlier"
            >
              <icons.previous size={16} aria-hidden />
            </Button>
            <Button
              variant="quiet"
              className="small-btn"
              onClick={() => go({ from: operationalToday(zone) })}
            >
              Today
            </Button>
            <Button
              variant="quiet"
              className="small-btn"
              onClick={() => move(1)}
              aria-label="Later"
            >
              <icons.next size={16} aria-hidden />
            </Button>
          </div>
          <div className="seg" role="radiogroup" aria-label="Days in view">
            {ROSTER_DAYS.map((n) => (
              <label key={n} className="seg-opt">
                <input
                  type="radio"
                  name="roster-days"
                  checked={days === n}
                  onChange={() => go({ days: n })}
                />
                {n === 1 ? '1 day' : `${n} days`}
              </label>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-muted">
            Job
            <select
              className="input w-42! py-1! text-[13px]!"
              value={search.job ?? ''}
              onChange={(e) => go({ job: e.currentTarget.value || undefined })}
            >
              <option value="">All jobs</option>
              {(jobs.data ?? [])
                .filter((j) => j.status !== 'cancelled' || j.id === search.job)
                .map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.reference} — {j.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-xs text-muted">
            Venue
            <select
              className="input w-38! py-1! text-[13px]!"
              value={search.venue ?? ''}
              onChange={(e) => go({ venue: e.currentTarget.value || undefined })}
            >
              <option value="">All venues</option>
              {(venues.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {mayManage ? (
            <Button
              variant="quiet"
              className="small-btn"
              aria-pressed={selecting}
              onClick={() => {
                setSelecting(!selecting);
                setPicked(null);
                setOutcome(null);
              }}
            >
              Select
            </Button>
          ) : null}
          <Button
            variant="quiet"
            className="small-btn"
            disabled={page.isFetching}
            onClick={() => void page.refetch()}
          >
            Refresh
          </Button>
          {updated ? <span className="text-[11px] text-muted">Updated {updated}</span> : null}
        </div>
      </div>
      {outcome ? <OutcomeCallout outcome={outcome} /> : null}
      <MoveCallout drag={drag} />
      <MoveLine drag={drag} />
      {failed ? (
        <p role="alert" className="alert">
          {failed.message}
        </p>
      ) : null}
      {page.isPending ? <p className="text-muted">Loading…</p> : null}
      {page.isError ? (
        <p role="alert" className="alert">
          {page.error.message}
        </p>
      ) : null}
      {data ? (
        <div>
          <div className="roster-box">
            <div
              className="roster"
              data-picking={selecting && picked?.kind === 'task' ? 'tasks' : undefined}
              style={{ minWidth: `calc(var(--roster-label) + ${days * DAY_WIDTH[days]}px)` }}
            >
              <TimelineOverlay span={span} zone={zone} />
              <TimelineHead span={span} zone={zone} every={every} hours={days < 14} />
              <GroupRow
                label="Tasks"
                meta={
                  data.tasks.length === 0
                    ? 'None then'
                    : [
                        `${data.tasks.length} across ${plural(bands.filter((b) => b.job).length, 'job')}`,
                        partFilled ? `${partFilled} part-filled` : '',
                        doneCount ? `${doneCount} done` : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')
                }
              />
              {bands.map((band) => (
                <TrackRow
                  key={band.key}
                  kind="job"
                  cols={cols}
                  track={drag.gestures.trackProps(`job:${band.job?.id ?? 'none'}`)}
                  overlay={
                    <TrackOverlay
                      drag={drag}
                      track={`job:${band.job?.id ?? 'none'}`}
                      barKeys={band.tasks.map((t) => `task:${t.id}`)}
                      span={span}
                      zone={zone}
                      makes={['task']}
                      onMake={(what, drawn) =>
                        setOpened({ kind: 'add', what, drawn: { ...drawn, jobId: band.job?.id } })
                      }
                    />
                  }
                  label={
                    <BandLabel
                      band={band}
                      client={jobs.data?.find((j) => j.id === band.job?.id)?.client.name ?? null}
                      leadId={
                        jobs.data?.find((j) => j.id === band.job?.id)?.leadCrewMemberId ?? null
                      }
                      people={data.people}
                      mayLead={mayLead}
                      onChanged={refresh}
                    />
                  }
                  items={band.tasks.map((t): Placed => ({
                    key: t.id,
                    startsAt: t.window.startsAt,
                    endsAt: t.window.endsAt,
                    node: (
                      <TaskBar
                        task={t}
                        style={drag.styleFor(`task:${t.id}`, t.window)}
                        gesture={drag.gestures.barProps(`task:${t.id}`, drag.windowOf(t.window), {
                          move: t.status !== 'done',
                          resize: t.status !== 'done',
                        })}
                        selected={isPicked('task', t.id)}
                        onClick={() => onTask(t.id)}
                      />
                    ),
                  }))}
                />
              ))}
              <GroupRow
                label="People"
                meta={`${plural(data.people.length, 'person', 'people')} on the crew list · ${busyCount} with something in view, then the rest by name`}
              />
              {rows.map((row) => {
                const person = data.people.find((p) => p.id === row.key);
                const roles = [...new Set(row.shifts.map((s) => s.role?.name).filter(Boolean))];
                const hours = hoursInView(row.shifts, span);
                const timeOff = row.sub
                  ? []
                  : (data.timeOff ?? []).filter((t) => t.crewMemberId === row.key);
                return (
                  <TrackRow
                    key={row.key}
                    kind="crew"
                    cols={cols}
                    behind={timeOff.map((t) => (
                      <button
                        key={`${t.id}-${t.startsAt}`}
                        type="button"
                        className="roster-time-off"
                        data-kind={t.kind}
                        data-pending={t.pending ? 'true' : undefined}
                        style={barStyle(span, t)}
                        title={`${timeOffTitle(t)} · ${timeOffWhen(t, zone)}`}
                        aria-label={`${timeOffTitle(t)}${t.pending ? ', not yet approved' : ''} · ${timeOffWhen(t, zone)}`}
                        onClick={() =>
                          setOpened({ kind: 'timeOff', id: t.id, startsAt: t.startsAt })
                        }
                      />
                    ))}
                    track={row.sub ? undefined : drag.gestures.trackProps(`person:${row.key}`)}
                    overlay={
                      row.sub ? null : (
                        <TrackOverlay
                          drag={drag}
                          track={`person:${row.key}`}
                          barKeys={[
                            ...row.shifts.map((s) => `shift:${s.id}`),
                            ...row.offers.map((o) => `offer:${o.id}`),
                          ]}
                          span={span}
                          zone={zone}
                          makes={
                            mayCrew
                              ? ['shift', 'offer', 'unavailable', 'leave']
                              : ['shift', 'offer']
                          }
                          onMake={(what, drawn) =>
                            setOpened({
                              kind: 'add',
                              what,
                              drawn: { ...drawn, crewMemberId: row.key, jobId: search.job },
                            })
                          }
                        />
                      )
                    }
                    label={
                      <>
                        <span className="roster-mono" aria-hidden="true">
                          {row.sub ? '—' : initials(row.label)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-semibold leading-tight">
                            {row.label}
                          </span>
                          <span className="block truncate text-[10px] text-muted">
                            {row.sub ??
                              [roles.join(', '), person ? CREW_CLASS[person.class] : '']
                                .filter(Boolean)
                                .join(' · ')}
                          </span>
                        </span>
                        {row.shifts.length > 0 && !row.sub ? (
                          <span className="roster-in-view text-[11px] text-muted">
                            {hours.toFixed(2)}h
                          </span>
                        ) : null}
                      </>
                    }
                    items={[
                      ...row.shifts.map((s): Placed => ({
                        key: s.id,
                        startsAt: s.window.startsAt,
                        endsAt: s.window.endsAt,
                        node: (
                          <ShiftBar
                            shift={s}
                            style={drag.styleFor(`shift:${s.id}`, s.window)}
                            gesture={drag.gestures.barProps(
                              `shift:${s.id}`,
                              drag.windowOf(s.window),
                              // A cancelled job's shift, or one cancelled or declined, stays put.
                              movable(s),
                            )}
                            selected={isPicked('shift', s.id)}
                            overTimeOff={overTimeOff(s, timeOff)}
                            onClick={() => onShift(s)}
                          />
                        ),
                      })),
                      ...row.offers.map((o): Placed => ({
                        key: `offer-${o.id}`,
                        startsAt: o.window.startsAt,
                        endsAt: o.window.endsAt,
                        node: (
                          <OfferBar
                            offer={o}
                            style={drag.styleFor(`offer:${o.id}`, o.window)}
                            gesture={drag.gestures.barProps(
                              `offer:${o.id}`,
                              drag.windowOf(o.window),
                              { move: true, resize: true },
                            )}
                            onClick={() => setOpened({ kind: 'offer', id: o.id })}
                          />
                        ),
                      })),
                    ]}
                  />
                );
              })}
            </div>
          </div>
          <RosterKey />
        </div>
      ) : null}
      {selecting ? (
        <div className="action-bar" role="region" aria-label="Selected">
          <span className="font-heading text-sm font-extrabold">
            {!picked ? 'Tick tasks or shifts' : plural(picked.ids.length, picked.kind) + ' ticked'}
          </span>
          {picked?.kind === 'task' ? (
            <span className="text-[13px] opacity-85">Click a shift to put them all on it</span>
          ) : null}
          <span className="flex-1" />
          {picked?.kind === 'task' ? (
            <Button
              variant="quiet"
              className="small-btn on-dark"
              disabled={clear.isPending}
              onClick={() => clear.mutate(picked.ids)}
            >
              Take everyone off
            </Button>
          ) : null}
          {picked?.kind === 'shift' ? (
            <>
              <Button
                className="small-btn"
                disabled={bulk.isPending}
                onClick={() => bulk.mutate({ action: 'publish', shiftIds: picked.ids })}
              >
                Publish
              </Button>
              <Button
                variant="quiet"
                className="small-btn on-dark"
                disabled={bulk.isPending}
                onClick={() => bulk.mutate({ action: 'confirm', shiftIds: picked.ids })}
              >
                Confirm
              </Button>
              <Button
                variant="quiet"
                className="small-btn on-dark"
                onClick={() => setOpened({ kind: 'cancel' })}
              >
                Cancel…
              </Button>
            </>
          ) : null}
          {picked ? (
            <Button variant="quiet" className="small-btn on-dark" onClick={() => setPicked(null)}>
              Clear selection
            </Button>
          ) : null}
        </div>
      ) : null}
      {opened ? (
        <RosterPopover
          opened={opened}
          date={date}
          data={data}
          mayManage={mayManage}
          mayCrew={mayCrew}
          mayApprove={mayApprove}
          zone={zone}
          seesPrices={seesPrices}
          picked={picked}
          busy={assign.isPending || bulk.isPending}
          onAssignAnyway={(shiftId, taskIds) => assign.mutate({ taskIds, shiftId, confirm: true })}
          onCancelShifts={(shiftIds) => bulk.mutate({ action: 'cancel', shiftIds })}
          onChanged={refresh}
          onClose={close}
        />
      ) : null}
    </div>
  );
}

/** A job's band: its reference and name, its client and venue, and its lead (CRW-11). */
function BandLabel({
  band,
  client,
  leadId,
  people,
  mayLead,
  onChanged,
}: {
  band: TaskBand;
  client: string | null;
  leadId: string | null;
  people: ReadonlyArray<{ id: string; name: string }>;
  mayLead: boolean;
  onChanged: () => Promise<void>;
}) {
  const job = band.job;
  const save = useMutation({
    mutationFn: (leadCrewMemberId: string | null) => updateJob(job!.id, { leadCrewMemberId }),
    onSuccess: onChanged,
  });
  if (!job) {
    return (
      <>
        <span className="block truncate font-semibold">No job</span>
        <span className="block truncate text-[11px] text-muted">{venueOf(band.tasks) ?? ''}</span>
      </>
    );
  }
  // The lead is someone on one of the job's tasks (CRW-11 at v1.997).
  const onTasks = new Map<string, string>();
  for (const t of band.tasks) {
    for (const a of t.assignments)
      if (a.crewMember) onTasks.set(a.crewMember.id, a.crewMember.name);
  }
  const leadName = leadId
    ? (onTasks.get(leadId) ?? people.find((p) => p.id === leadId)?.name ?? 'Someone')
    : null;
  const title = `J-${job.number} — ${job.name}`;
  return (
    <>
      <span className="block truncate font-semibold leading-tight">{title}</span>
      <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted">
        <span className="truncate">
          {job.cancelled ? 'Cancelled · ' : ''}
          {[client, venueOf(band.tasks)].filter(Boolean).join(' · ')}
        </span>
        {mayLead && !job.cancelled ? (
          <select
            className="roster-lead"
            aria-label={`Lead for ${title}`}
            value={leadId ?? ''}
            disabled={save.isPending}
            onChange={(e) => save.mutate(e.currentTarget.value || null)}
          >
            <option value="">Lead: none</option>
            {leadId && !onTasks.has(leadId) ? (
              <option value={leadId}>Lead: {leadName}</option>
            ) : null}
            {[...onTasks].map(([id, name]) => (
              <option key={id} value={id}>
                Lead: {name}
              </option>
            ))}
          </select>
        ) : (
          <span className="shrink-0">Lead: {leadName ?? 'none'}</span>
        )}
      </span>
      {save.isError ? (
        <span role="alert" className="text-[11px] text-accent-2-700">
          {save.error.message}
        </span>
      ) : null}
    </>
  );
}

/** What a bulk action did, and what it refused, by name (the design's "1 refused" callout). */
function OutcomeCallout({ outcome }: { outcome: Outcome }) {
  return (
    <div role="status" className="callout">
      {outcome.refused.length > 0 ? (
        <span className="tag tag-accent-2 shrink-0">{outcome.refused.length} refused</span>
      ) : null}
      <span>
        {outcome.done}
        {outcome.refused.map((r) => (
          <span key={r.name}>
            {' '}
            <strong>{r.name}</strong> was not: {r.reason}
          </span>
        ))}
      </span>
    </div>
  );
}

/** The venue most of a band's tasks are at. */
/** Whether a shift can be dragged: not on a cancelled job, not cancelled or declined. */
function movable(s: Shift): { move: boolean; resize: boolean } {
  const can = !s.bookedForJob?.cancelled && s.state !== 'cancelled' && s.state !== 'declined';
  return { move: can, resize: can };
}

function venueOf(tasks: readonly Task[]): string | null {
  const counts = new Map<string, number>();
  for (const t of tasks) if (t.venue) counts.set(t.venue.name, (counts.get(t.venue.name) ?? 0) + 1);
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Today's operational date: before 05:00 it is still last night. */
function operationalToday(zone: string): string {
  const now = Temporal.Now.zonedDateTimeISO(zone);
  return (now.hour < 5 ? now.subtract({ days: 1 }) : now).toPlainDate().toString();
}

/** The URL keeps only what is set. */
function strip(search: { [K in keyof RosterSearch]?: RosterSearch[K] | undefined }): RosterSearch {
  return Object.fromEntries(
    Object.entries(search).filter(([, v]) => v !== undefined && v !== ''),
  ) as RosterSearch;
}

/** CRW-02 at v1.9106: what of the person's time off a live shift overlaps, in words; else nothing. */
function overTimeOff(s: Shift, timeOff: readonly TimeOffEntry[]): string | undefined {
  if (s.state === 'cancelled' || s.state === 'declined') return undefined;
  const at = Date.parse;
  const over = timeOff.filter(
    (t) => at(t.startsAt) < at(s.window.endsAt) && at(s.window.startsAt) < at(t.endsAt),
  );
  return over.length ? [...new Set(over.map(timeOffTitle))].join(', ') : undefined;
}
